import { useMemo, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router-dom";
import s from "./Compliance.module.css";
import { VerifyRuleDialog } from "./VerifyRuleDialog";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useCompliance, useRules } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  changedParameters,
  OBLIGATION_STATUS_LABEL,
  OBLIGATION_STATUSES,
  overallReadiness,
  sortObligations,
  type ObligationStatus,
  type Readiness,
  type Rule,
} from "../../domain/compliance";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  numClass,
  Segmented,
  Select,
  SeverityBadge,
  Skeleton,
  Table,
} from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { t } from "../../i18n";

type View = "obligations" | "rules";

/**
 * Certification readiness as a query, not a scramble the week before an
 * audit: readiness by framework, every obligation the packs materialised
 * (met, pending, overdue, waived — the pending and overdue ones are the
 * point), and which rules are live or still gated behind verification.
 */
export function CompliancePage() {
  const { site } = useSession();
  const [params, setParams] = useSearchParams();
  const view = (params.get("view") as View) || "obligations";
  const status = (params.get("status") as ObligationStatus | null) || null;
  const framework = params.get("framework") || null;

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const report = useCompliance();
  const rules = useRules();

  const readiness = useMemo(() => report.data?.readiness ?? [], [report.data]);
  const overall = useMemo(() => overallReadiness(readiness), [readiness]);
  const rows = useMemo(
    () =>
      sortObligations(report.data?.obligations ?? []).filter(
        (o) => (!status || o.status === status) && (!framework || o.framework === framework),
      ),
    [report.data, status, framework],
  );
  const openCount = (report.data?.obligations ?? []).filter(
    (o) => o.status === "overdue" || o.status === "pending",
  ).length;

  return (
    <Page
      eyebrow={t("{site} · compliance", { site: site.code })}
      title="Compliance"
      actions={
        <Segmented<View>
          ariaLabel="View"
          value={view}
          options={[
            { value: "obligations", label: "Obligations" },
            { value: "rules", label: "Rules" },
          ]}
          onChange={(v) => set("view", v === "obligations" ? null : v)}
        />
      }
    >
      {report.isPending ? (
        <Skeleton rows={3} height={60} />
      ) : report.isError ? (
        <ErrorState error={report.error} retry={() => void report.refetch()} />
      ) : (
        <div className={s.frameworks}>
          {overall && <FrameworkCard r={overall} emphasis />}
          {readiness.map((r) => (
            <FrameworkCard
              key={r.framework}
              r={r}
              onClick={() => set("framework", framework === r.framework ? null : r.framework)}
              active={framework === r.framework}
            />
          ))}
          {readiness.length === 0 && (
            <EmptyState>
              {t("No obligations at {site} yet. The packs materialise them when the insight engine runs.", {
                site: site.code,
              })}
            </EmptyState>
          )}
        </div>
      )}

      {view === "obligations" ? (
        <Card
          title="Obligations"
          sub={t("{n} open · every period is listed whether or not it was met, so what is missing shows", {
            n: openCount,
          })}
        >
          <div className={s.filters} style={{ marginBottom: 10 }}>
            <Select
              value={status ?? ""}
              onChange={(e) => set("status", e.target.value || null)}
              aria-label={t("Status")}
            >
              <option value="">{t("Any status")}</option>
              {OBLIGATION_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {t(OBLIGATION_STATUS_LABEL[st])}
                </option>
              ))}
            </Select>
            <Select
              value={framework ?? ""}
              onChange={(e) => set("framework", e.target.value || null)}
              aria-label={t("Framework")}
            >
              <option value="">{t("Any framework")}</option>
              {readiness.map((r) => (
                <option key={r.framework} value={r.framework}>
                  {r.framework}
                </option>
              ))}
            </Select>
            <span className={s.count}>
              {report.data ? t("{n} of {total}", { n: rows.length, total: report.data.obligations.length }) : ""}
            </span>
          </div>
          {report.isPending ? (
            <Skeleton rows={6} />
          ) : rows.length === 0 ? (
            <EmptyState>No obligations match.</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t("Status")}</th>
                  <th>{t("Obligation")}</th>
                  <th>{t("Framework")}</th>
                  <th>{t("Period")}</th>
                  <th>{t("Due")}</th>
                  <th>{t("Met")}</th>
                  <th style={{ textAlign: "end" }}>{t("Evidence")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o, i) => (
                  <tr key={`${o.framework}-${o.title}-${o.periodKey ?? i}`}>
                    <td>
                      <StatusPill status={o.status} />
                    </td>
                    <td>{o.title}</td>
                    <td className="muted">{o.framework}</td>
                    <td className="mono">
                      {o.periodKey ?? "—"}
                      {o.period && <span className="muted"> · {o.period}</span>}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{o.dueAt ? formatDate(o.dueAt) : "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {o.metAt ? formatDateTime(o.metAt) : <span className="muted">—</span>}
                    </td>
                    <td className={numClass}>{o.evidenceCount}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      ) : (
        <Card
          title="Rules"
          sub="as this tenant runs them — a gated rule produces no alerts until its thresholds are confirmed against the certification basis"
        >
          {rules.isPending ? (
            <Skeleton rows={5} height={40} />
          ) : rules.isError ? (
            <ErrorState error={rules.error} retry={() => void rules.refetch()} />
          ) : (
            <div className={s.rules}>
              {rules.data.map((r) => (
                <RuleRow key={r.key} r={r} />
              ))}
            </div>
          )}
        </Card>
      )}
    </Page>
  );
}

function readinessColour(pct: number, overdue: number): string {
  if (overdue > 0 && pct < 90) return "var(--nogo)";
  if (overdue > 0 || pct < 95) return "var(--caution)";
  return "var(--go)";
}

function FrameworkCard({
  r,
  emphasis,
  onClick,
  active,
}: {
  r: Readiness;
  emphasis?: boolean;
  onClick?: () => void;
  active?: boolean;
}) {
  const c = readinessColour(r.pct, r.overdue);
  return (
    <div
      className={s.fw}
      style={
        {
          "--fw-c": c,
          cursor: onClick ? "pointer" : undefined,
          outline: active ? "2px solid var(--blue)" : undefined,
        } as CSSProperties
      }
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && e.key === "Enter") onClick();
      }}
      data-testid={emphasis ? "readiness-overall" : undefined}
    >
      <div className={s.fwName}>{emphasis ? t(r.framework) : r.framework}</div>
      <div className={s.fwPct}>{r.pct.toFixed(1)}%</div>
      <div className={s.fwBar}>
        <div className={s.fwFill} style={{ width: `${r.pct}%` }} />
      </div>
      <div className={s.fwMeta}>
        <span>{t("{met} of {total} met", { met: r.met, total: r.total })}</span>
        {r.overdue > 0 && (
          <span>
            <b>{t("{n} overdue", { n: r.overdue })}</b>
          </span>
        )}
      </div>
    </div>
  );
}

const STATUS_COLOUR: Record<ObligationStatus, string> = {
  overdue: "var(--red)",
  pending: "var(--amber)",
  met: "var(--green)",
  waived: "var(--dim)",
};

export function StatusPill({ status }: { status: ObligationStatus }) {
  return (
    <span
      className={`${s.status} ${status === "overdue" ? s.statusSolid : ""}`}
      style={{ "--st-c": STATUS_COLOUR[status] } as CSSProperties}
    >
      {t(OBLIGATION_STATUS_LABEL[status])}
    </span>
  );
}

function RuleRow({ r }: { r: Rule }) {
  const { principal } = useSession();
  const [verifying, setVerifying] = useState(false);
  const canVerify = hasPermission(principal, PERMISSION.ruleVerify);
  const changed = changedParameters(r);
  return (
    <div className={`${s.rule} ${r.enabled ? "" : s.ruleGated}`} data-testid={`rule-${r.key}`}>
      <SeverityBadge severity={r.severity} />
      <div>
        <div className={s.ruleName}>{r.name}</div>
        <div className={s.ruleMeta}>
          <span>{r.key}</span>
          {r.ruleClass && <span>{r.ruleClass}</span>}
          {r.kind && <span>{r.kind}</span>}
          {r.trigger && <span>{t("on {trigger}", { trigger: r.trigger })}</span>}
        </div>
        {r.reference && <div className={s.ruleRef}>{r.reference}</div>}
        <div className={s.ruleParams}>
          {Object.entries(r.parameters).map(([k, v]) => (
            <span
              key={k}
              className={`${s.param} ${changed.includes(k) ? s.paramChanged : ""}`}
              title={
                changed.includes(k)
                  ? t("Pack default: {value}", { value: JSON.stringify(r.packDefaults[k]) })
                  : undefined
              }
            >
              {k} = {JSON.stringify(v)}
            </span>
          ))}
        </div>
        {!r.enabled && (
          <div className={s.gatedNote}>{t("Gated — {reason}.", { reason: r.gatedReason ?? t("disabled") })}</div>
        )}
        {r.enabled && r.verifyRequired && r.confirmedAgainst && (
          <div className={s.verified}>
            {r.verifiedAt
              ? t("Thresholds confirmed against {basis} on {date}.", {
                  basis: r.confirmedAgainst,
                  date: formatDate(r.verifiedAt),
                })
              : t("Thresholds confirmed against {basis}.", { basis: r.confirmedAgainst })}
          </div>
        )}
      </div>
      <div style={{ display: "grid", gap: 6, justifyItems: "end", alignContent: "start" }}>
        <span className={s.status} style={{ "--st-c": r.enabled ? "var(--green)" : "var(--amber)" } as CSSProperties}>
          {r.enabled ? t("Live") : t("Gated")}
        </span>
        {(!r.enabled || r.verifyRequired) && (
          <Button
            onClick={() => setVerifying(true)}
            disabled={!canVerify}
            title={canVerify ? undefined : t("Needs the rule:verify permission")}
            aria-label={
              r.enabled ? t("Re-confirm {rule}", { rule: r.name }) : t("Verify and enable {rule}", { rule: r.name })
            }
          >
            {r.enabled ? t("Re-confirm") : t("Verify and enable")}
          </Button>
        )}
      </div>
      {verifying && (
        <VerifyRuleDialog rule={r} onClose={() => setVerifying(false)} onVerified={() => setVerifying(false)} />
      )}
    </div>
  );
}
